import { useMemo } from 'react';
import { useQuery, useDeleteMutation } from '@supabase-cache-helpers/postgrest-swr';

import { supabase } from 'src/auth/context/supabase/lib';

import {
  calculateRebootImpact,
  calculateEndOfLifeImpact,
  calculateEndOfLifeImpactWithLifespan,
} from 'src/sections/modules/ges/helper';

// ----------------------------------------------------------------------

export function useGetAllReuseFromEntity(entity_id) {
  const { data, isLoading, error, isValidating } = useQuery(
    entity_id
      ? supabase
          .from('reuse')
          .select(
            `
            id,
            created_at,
            name,
            inventory_id,
            target_inventory_id,
            entity_id,
            inventories!reuse_inventory_id_fkey (
              id,
              name
            ),
            target_inventory:inventories!public_reuse_target_inventory_id_fkey (
              id,
              name
            )
          `
          )
          .eq('entity_id', entity_id)
      : null
  );

  const memoizedValue = useMemo(
    () => ({
      reuses: data || [],
      reusesLoading: isLoading,
      reusesError: error,
      reusesValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------

export function useGetReuseById(reuse_id) {
  const { data, isLoading, error, isValidating } = useQuery(
    reuse_id
      ? supabase
          .from('reuse')
          .select(
            `
            id,
            created_at,
            name,
            inventory_id,
            target_inventory_id,
            entity_id,
            inventories!reuse_inventory_id_fkey (
              id,
              name
            ),
            target_inventory:inventories!public_reuse_target_inventory_id_fkey (
              id,
              name
            )
          `
          )
          .eq('id', reuse_id)
          .single()
      : null
  );

  const memoizedValue = useMemo(
    () => ({
      reuse: data || null,
      reuseLoading: isLoading,
      reuseError: error,
      reuseValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------

export function useGetInventoryLinesWithReuse(inventory_id, reuse_id) {
  const { data: reuseLinesData } = useQuery(
    reuse_id ? supabase.from('reuse_line').select('*').eq('reuse_id', reuse_id) : null
  );

  const { data, error, isLoading, mutate } = useQuery(
    inventory_id && reuse_id
      ? supabase
          .from('inventory_line')
          .select(
            `
            id,
            quantity,
            average_lifespan,
            manufacturing_impact,
            annual_use_impact,
            total_annual_impact,
            pue,
            data_repository (
              id, 
              name, 
              category, 
              subcategory, 
              manufacturing_impact, 
              annual_use_impact, 
              unit, 
              source, 
              comment, 
              order, 
              ask_pue, 
              data_categories (id, name, color), 
              data_subcategories (id, name),
              reboot_impact,
              end_of_life_impact
            )
          `
          )
          .eq('inventory_id', inventory_id)
      : null
  );

  const transformedData = useMemo(() => {
    if (!data) return [];

    return data.map((line) => {
      const reuseLine = reuseLinesData?.find((rl) => rl.inventory_line_id === line.id);
      return {
        ...line,
        reuse_line: reuseLine || null,
      };
    });
  }, [data, reuseLinesData]);

  return {
    inventoryLines: transformedData || [],
    error,
    isLoading,
    mutate,
  };
}

export function useDeleteReuseLine() {
  const { trigger } = useDeleteMutation(supabase.from('reuse_line').select('*'), ['id']);
  return { trigger };
}

export function useGetBaseEndOfLife(inventory_id) {
  const { data, isLoading, error } = useQuery(
    inventory_id
      ? supabase
          .from('inventory_line')
          .select(
            `
            id,
            quantity,
            average_lifespan,
            data_repository (
              manufacturing_impact,
              end_of_life_impact
            )
          `
          )
          .eq('inventory_id', inventory_id)
      : null
  );

  const memoizedValue = useMemo(() => {
    let baseEndOfLife = 0;

    if (data) {
      data.forEach((line) => {
        const { quantity, average_lifespan, data_repository } = line;
        const manufacturingImpact = data_repository.manufacturing_impact * quantity;

        baseEndOfLife += calculateEndOfLifeImpactWithLifespan({
          manufacturingImpact,
          endOfLifeImpactPercentage: data_repository.end_of_life_impact,
          averageLifespan: average_lifespan,
        });
      });
    }

    return {
      baseEndOfLife,
      baseEndOfLifeLoading: isLoading,
      baseEndOfLifeError: error,
    };
  }, [data, isLoading, error]);

  return memoizedValue;
}

export function useGetReuseSavings(inventory_id) {
  // D'abord, trouver le reuse qui a cet inventaire comme cible
  const { data: reuse } = useQuery(
    inventory_id
      ? supabase
          .from('reuse')
          .select('id, inventory_id')
          .eq('target_inventory_id', inventory_id)
          .maybeSingle()
      : null
  );

  // Ensuite, obtenir les lignes d'inventaire de l'inventaire source
  const { data, isLoading, error } = useQuery(
    reuse?.inventory_id
      ? supabase
          .from('inventory_line')
          .select(
            `
            id,
            quantity,
            average_lifespan,
            data_repository (
              manufacturing_impact,
              reboot_impact,
              end_of_life_impact
            ),
            reuse_line!reuse_line_inventory_line_id_fkey (
              reuse_percentage,
              waste_percentage,
              backup_percentage
            )
          `
          )
          .eq('inventory_id', reuse.inventory_id)
      : null
  );

  const memoizedValue = useMemo(() => {
    let totalRebootSavings = 0;
    let totalEndOfLifeSavings = 0;

    if (data) {
      data.forEach((line) => {
        const {
          quantity,
          average_lifespan,
          data_repository,
          reuse_line: [reuseLine],
        } = line;

        if (!reuseLine) return;

        const manufacturingImpact = data_repository.manufacturing_impact * quantity;

        const endOfLife = calculateEndOfLifeImpact({
          manufacturingImpact: manufacturingImpact * (reuseLine.reuse_percentage / 100),
          endOfLifeImpactPercentage: data_repository.end_of_life_impact,
          reuseLine,
          forceFullWaste: true,
        });

        totalRebootSavings += calculateRebootImpact({
          manufacturingImpact,
          rebootImpactPercentage: data_repository.reboot_impact,
          reuseLine,
          averageLifespan: average_lifespan,
          endOfLifeImpact: endOfLife,
        });

        const reuseManufacturingImpact = manufacturingImpact * (reuseLine.reuse_percentage / 100);
        totalEndOfLifeSavings += calculateEndOfLifeImpact({
          manufacturingImpact: reuseManufacturingImpact,
          endOfLifeImpactPercentage: data_repository.end_of_life_impact,
          reuseLine,
          forceFullWaste: true,
        });
      });
    }

    return {
      rebootSavings: totalRebootSavings,
      endOfLifeSavings: totalEndOfLifeSavings,
      savingsLoading: isLoading,
      savingsError: error,
      sourceInventoryId: reuse?.inventory_id,
    };
  }, [data, isLoading, error, reuse]);

  return memoizedValue;
}

export function useGetReuseSavingsFromMotherEntity(entity_id) {
  // Obtenir les entités filles
  const { data: childEntities } = useQuery(
    entity_id ? supabase.from('entities').select('id').eq('parent', entity_id) : null
  );

  // Obtenir les inventaires actuels des entités filles
  const { data: currentInventories } = useQuery(
    childEntities?.length
      ? supabase
          .from('inventories')
          .select('id')
          .in(
            'entity_id',
            childEntities.map((e) => e.id)
          )
          .eq('is_current', true)
      : null
  );

  // Obtenir tous les reuses qui ont ces inventaires comme cible
  const { data: reuses } = useQuery(
    currentInventories?.length
      ? supabase
          .from('reuse')
          .select('id, inventory_id, target_inventory_id')
          .in(
            'target_inventory_id',
            currentInventories.map((inv) => inv.id)
          )
      : null
  );

  // Obtenir les lignes d'inventaire des inventaires sources
  const {
    data: savingsData,
    isLoading,
    error,
  } = useQuery(
    reuses?.length
      ? supabase
          .from('inventory_line')
          .select(
            `
            id,
            quantity,
            average_lifespan,
            inventory_id,
            data_repository (
              manufacturing_impact,
              reboot_impact,
              end_of_life_impact
            ),
            reuse_line!reuse_line_inventory_line_id_fkey (
              reuse_percentage,
              waste_percentage,
              backup_percentage
            )
          `
          )
          .in(
            'inventory_id',
            reuses.map((r) => r.inventory_id)
          )
      : null
  );

  const memoizedValue = useMemo(() => {
    let totalRebootSavings = 0;
    let totalEndOfLifeSavings = 0;

    if (savingsData) {
      savingsData.forEach((line) => {
        const {
          quantity,
          average_lifespan,
          data_repository,
          reuse_line: [reuseLine],
        } = line;

        if (!reuseLine) return;

        const manufacturingImpact = data_repository.manufacturing_impact * quantity;

        const endOfLife = calculateEndOfLifeImpact({
          manufacturingImpact: manufacturingImpact * (reuseLine.reuse_percentage / 100),
          endOfLifeImpactPercentage: data_repository.end_of_life_impact,
          reuseLine,
          forceFullWaste: true,
        });

        totalRebootSavings += calculateRebootImpact({
          manufacturingImpact,
          rebootImpactPercentage: data_repository.reboot_impact,
          reuseLine,
          averageLifespan: average_lifespan,
          endOfLifeImpact: endOfLife,
        });

        const reuseManufacturingImpact = manufacturingImpact * (reuseLine.reuse_percentage / 100);
        totalEndOfLifeSavings += calculateEndOfLifeImpact({
          manufacturingImpact: reuseManufacturingImpact,
          endOfLifeImpactPercentage: data_repository.end_of_life_impact,
          reuseLine,
          forceFullWaste: true,
        });
      });
    }

    return {
      rebootSavings: totalRebootSavings,
      endOfLifeSavings: totalEndOfLifeSavings,
      savingsLoading: isLoading,
      savingsError: error,
    };
  }, [savingsData, isLoading, error]);

  return memoizedValue;
}

// Nouvelle fonction pour obtenir le baseEndOfLife des entités filles
export function useGetBaseEndOfLifeFromMotherEntity(inventory_id) {
  // Obtenir d'abord l'inventaire parent
  const { data: parentInventory } = useQuery(
    inventory_id
      ? supabase.from('inventories').select('id, entity_id').eq('id', inventory_id).maybeSingle()
      : null
  );

  // Obtenir les entités filles de l'entité parente
  const { data: childEntities } = useQuery(
    parentInventory?.entity_id
      ? supabase.from('entities').select('id').eq('parent', parentInventory.entity_id)
      : null
  );

  // Obtenir les inventaires actuels des entités filles
  const { data: currentInventories } = useQuery(
    childEntities?.length
      ? supabase
          .from('inventories')
          .select('id')
          .in(
            'entity_id',
            childEntities.map((e) => e.id)
          )
          .eq('is_current', true)
      : null
  );

  const { data, isLoading, error } = useQuery(
    currentInventories?.length
      ? supabase
          .from('inventory_line')
          .select(
            `
            id,
            quantity,
            average_lifespan,
            data_repository (
              manufacturing_impact,
              end_of_life_impact
            )
          `
          )
          .in(
            'inventory_id',
            currentInventories.map((inv) => inv.id)
          )
      : null
  );

  const memoizedValue = useMemo(() => {
    let baseEndOfLife = 0;

    if (data) {
      data.forEach((line) => {
        const { quantity, average_lifespan, data_repository } = line;
        const manufacturingImpact = data_repository.manufacturing_impact * quantity;

        baseEndOfLife += calculateEndOfLifeImpactWithLifespan({
          manufacturingImpact,
          endOfLifeImpactPercentage: data_repository.end_of_life_impact,
          averageLifespan: average_lifespan,
        });
      });
    }

    return {
      baseEndOfLife,
      baseEndOfLifeLoading: isLoading,
      baseEndOfLifeError: error,
    };
  }, [data, isLoading, error]);

  return memoizedValue;
}

export function useGetReuseByInventoryId(inventory_id) {
  const { data, isLoading, error } = useQuery(
    inventory_id
      ? supabase.from('reuse').select('id, inventory_id').eq('inventory_id', inventory_id).maybeSingle()
      : null
  );

  const memoizedValue = useMemo(
    () => ({
      reuse: data || null,
      reuseLoading: isLoading,
      reuseError: error,
    }),
    [data, error, isLoading]
  );

  return memoizedValue;
}
